import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';

import {
  PURCHASE_STORAGE,
  GET_COMPANY_BY_ID,
  GET_COMPANY_STORAGE_USAGES,
} from '../graphql';

import UsageTable from './custom-table';
import BuyStorageModal from './buy-storage-modal';

import {
  Title,
  Text,
  Button,
  StorageHeader,
} from './styles';
import formatBytes from '../../../../core/utils/format-bytes';

const Storage = ({ companyId }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [token, setToken] = useState(null);
  const { data: { company } = {} } = useQuery(GET_COMPANY_BY_ID, {
    variables: { id: companyId },
    skip: !companyId,
  });

  const { data: storageUsageLogs } = useQuery(GET_COMPANY_STORAGE_USAGES, {
    variables: { companyId },
    skip: !companyId,
  });

  const totalStorage = (company?.uploadResearchTotalStorage || 0);
  const availableStorage = formatBytes(company?.uploadResearchAvailableStorage || 0);

  const [purchaseStorage] = useMutation(PURCHASE_STORAGE, {
    refetchQueries: [{
      query: GET_COMPANY_BY_ID,
      variables: { id: companyId },
    }],
  });

  const handleStripeCallback = (newToken) => {
    setToken(newToken);
  };

  const handleBuyStorage = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setPurchaseError(null);
    setToken(null);
  };

  const handleSubmitPurchase = async (amount, rememberCard, paymentMethodId) => {
    setPurchaseLoading(true);
    try {
      await purchaseStorage({
        variables: {
          input: {
            companyId,
            storageAmount: amount,
            token,
            paymentMethodId,
            rememberCard,
          },
        },
      });
      setPurchaseLoading(false);
      setModalOpen(false);
      setToken(null);
    } catch (err) {
      setPurchaseLoading(false);
      setPurchaseError(err.message);
    }
  };

  return (
    <>
      <StorageHeader>
        <Title as="h2" data-title-past-uploads>Past Uploads</Title>
        <Button
          data-buy-more-storage
          onClick={handleBuyStorage}
        >
          Buy more storage
        </Button>
      </StorageHeader>
      <Text>
        {`Storage remaining: ${availableStorage} of ${totalStorage} GB`}
      </Text>

      {storageUsageLogs && (
        <UsageTable
          dataList={storageUsageLogs.companyStorageUsage}
        />
      )}

      <BuyStorageModal
        companyId={companyId}
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitPurchase}
        callback={handleStripeCallback}
        error={purchaseError}
        loading={purchaseLoading}
        token={token}
      />
    </>
  );
};

Storage.propTypes = {
  companyId: PropTypes.string.isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

const enhance = compose(
  connect(mapStateToProps, null),
);

export default enhance(Storage);
